import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  FileText,
  Download,
  Filter,
  Eye,
  Users,
  Store,
  Calendar,
  Layers,
  CheckCircle,
  XCircle,
  Clock,
  Printer as PrintIcon,
  Search,
  RefreshCcw,
  Loader2,
  ChevronRight,
  Info,
  TrendingUp,
  DollarSign,
  Receipt,
  BarChart3,
  PieChart,
  Percent,
  Package,
} from "lucide-react";
import ReactApexChart from "react-apexcharts";
import apiClient from "../../api/apiClient";
import { API_BASE_URL, API_ENDPOINTS } from "../../api/header";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageContainer } from "@/components/layout/PageContainer";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { FilterCard } from "@/components/ui/filter-card";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ReactSelect } from "@/components/ui/react-select";
import { DataTable } from "@/components/ui/data-table";
import { Badge } from "@/components/ui/badge";

export const SalesSummaryReport = () => {
  const currentStore = useSelector((state) => state.current_store);
  const currencySymbol = currentStore?.default_currency?.symbol || "";

  const [filters, setFilters] = useState(() => ({
    customer_id: "",
    store_id: currentStore?.id ? String(currentStore.id) : "",
    user_id: "",
    start_date: "",
    end_date: "",
    status: "",
    payment_status: "",
    payment_method: "",
    group_by: "",
    sort_by: "date",
    sort_order: "desc",
    include_items: false,
  }));
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showFilters, setShowFilters] = useState(true);

  const prevStoreIdRef = useRef(currentStore?.id);

  useEffect(() => {
    if (currentStore?.id && currentStore.id !== prevStoreIdRef.current) {
      prevStoreIdRef.current = currentStore.id;
      setFilters((prev) => ({
        ...prev,
        store_id: String(currentStore.id),
      }));
    }
  }, [currentStore?.id]);

  const permissions = useSelector((state) => state.userpermission_data) || [];
  const hasPermission = (permission) => {
    if (!permission) return true;
    return permissions.includes(permission);
  };

  const showToast = (message, type) => {
    if (type === "error") toast.error(message);
    else if (type === "success") toast.success(message);
    else toast(message);
  };

  // Detect dark mode from localStorage
  useEffect(() => {
    const checkDarkMode = () => {
      const colorScheme = localStorage.getItem("colorschema");
      setIsDarkMode(colorScheme === "dark_mode");
    };

    checkDarkMode();
    const interval = setInterval(checkDarkMode, 500);
    window.addEventListener("storage", checkDarkMode);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", checkDarkMode);
    };
  }, []);

  // ==================== QUERIES ====================
  const { data: customers = [] } = useQuery({
    enabled: hasPermission("report sales summary view"),
    queryKey: ["customers"],
    queryFn: async () => {
      const response = await apiClient.get(`/customer`);
      return response.data.data;
    },
  });

  const { data: availablePaymentMethods = [] } = useQuery({
    queryKey: ["payment-methods-available", "sales", filters.store_id],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.PAYMENT_METHODS.AVAILABLE, {
        params: {
          scope: "sales",
          store_id: filters.store_id || undefined,
        },
      });
      return res.data?.data ?? res.data ?? [];
    },
    enabled: hasPermission("payment-method read"),
    staleTime: 5 * 60 * 1000,
  });

  const { data: stores = [] } = useQuery({
    enabled: hasPermission("report sales summary view"),
    queryKey: ["stores"],
    queryFn: async () => {
      const response = await apiClient.get(`/store`);
      return response.data.data;
    },
  });

  const { data: users = [] } = useQuery({
    enabled: hasPermission("report sales summary view"),
    queryKey: ["users"],
    queryFn: async () => {
      const response = await apiClient.get(`/user`);
      return response.data.data;
    },
  });

  // ==================== MUTATIONS ====================
  const previewReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report sales summary view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};
      if (currentFilters.customer_id)
        params.customer_id = currentFilters.customer_id;
      if (currentFilters.store_id) params.store_id = currentFilters.store_id;
      if (currentFilters.user_id) params.user_id = currentFilters.user_id;
      if (currentFilters.start_date)
        params.start_date = new Date(currentFilters.start_date).toISOString();
      if (currentFilters.end_date)
        params.end_date = new Date(currentFilters.end_date).toISOString();
      if (currentFilters.status && currentFilters.status !== "none")
        params.status = currentFilters.status;
      if (
        currentFilters.payment_status &&
        currentFilters.payment_status !== "none"
      )
        params.payment_status = currentFilters.payment_status;
      if (
        currentFilters.payment_method &&
        currentFilters.payment_method !== "none"
      )
        params.payment_method = currentFilters.payment_method;
      if (currentFilters.group_by && currentFilters.group_by !== "none")
        params.group_by = currentFilters.group_by;
      if (currentFilters.sort_by) params.sort_by = currentFilters.sort_by;
      if (currentFilters.sort_order)
        params.sort_order = currentFilters.sort_order;
      if (currentFilters.include_items) params.include_items = 1;

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.DATA.SALES_SUMMARY,
        {
          params,
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      setPreviewData(data);
      setShowFilters(false);
      showToast("Report preview loaded successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message || "Failed to load report preview",
        "error",
      );
    },
  });

  const generateReportMutation = useMutation({
    mutationFn: async (currentFilters) => {
      if (!hasPermission("report sales summary view")) {
        throw new Error("You do not have permission to perform this action.");
      }
      const params = {};
      if (currentFilters.customer_id)
        params.customer_id = currentFilters.customer_id;
      if (currentFilters.store_id) params.store_id = currentFilters.store_id;
      if (currentFilters.user_id) params.user_id = currentFilters.user_id;
      if (currentFilters.start_date)
        params.start_date = new Date(currentFilters.start_date).toISOString();
      if (currentFilters.end_date)
        params.end_date = new Date(currentFilters.end_date).toISOString();
      if (currentFilters.status && currentFilters.status !== "none")
        params.status = currentFilters.status;
      if (
        currentFilters.payment_status &&
        currentFilters.payment_status !== "none"
      )
        params.payment_status = currentFilters.payment_status;
      if (
        currentFilters.payment_method &&
        currentFilters.payment_method !== "none"
      )
        params.payment_method = currentFilters.payment_method;
      if (currentFilters.group_by && currentFilters.group_by !== "none")
        params.group_by = currentFilters.group_by;
      if (currentFilters.sort_by) params.sort_by = currentFilters.sort_by;
      if (currentFilters.sort_order)
        params.sort_order = currentFilters.sort_order;
      if (currentFilters.include_items) params.include_items = 1;

      const response = await apiClient.get(
        API_ENDPOINTS.REPORTS.SALES_SUMMARY,
        {
          params,
          responseType: "blob",
        },
      );
      return response.data;
    },
    onSuccess: (data) => {
      const blob = new Blob([data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setShowModal(true);
      showToast("Sales summary report generated successfully!", "success");
    },
    onError: (error) => {
      showToast(
        error.response?.data?.message ||
        "Failed to generate sales summary report",
        "error",
      );
    },
  });

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleGenerateReport = () => {
    showToast("Generating sales summary report...", "info");
    generateReportMutation.mutate(filters);
  };

  const handleClearFilters = () => {
    setShowFilters(true);
    setPreviewData(null);
    setFilters({
      customer_id: "",
      store_id: currentStore?.id ? String(currentStore.id) : "",
      user_id: "",
      start_date: "",
      end_date: "",
      status: "",
      payment_status: "",
      payment_method: "",
      group_by: "",
      sort_by: "date",
      sort_order: "desc",
      include_items: false,
    });
    setPreviewData(null);
  };

  const handleDownloadPDF = () => {
    if (pdfBlobUrl) {
      const link = document.createElement("a");
      link.href = pdfBlobUrl;
      link.download = `sales-summary-report-${new Date().getTime()}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showToast("PDF downloaded successfully!", "success");
    }
  };

  const handlePrintPDF = () => {
    if (pdfBlobUrl) {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = pdfBlobUrl;
      document.body.appendChild(iframe);
      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
        // Remove the iframe after the print dialog is closed
        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 1000);
      };
      showToast("Opening print dialog...", "info");
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (pdfBlobUrl) {
      window.URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  const customerOptions = customers.map((customer) => ({
    value: String(customer.id),
    label:
      customer.customer_type !== "individual" && customer.company
        ? customer.company
        : customer.full_name,
  }));

  const storeOptions = [
    { value: "", label: "All Locations" },
    ...stores.map((store) => ({
      value: String(store.id),
      label: store.name,
    })),
  ];

  const userOptions = users.map((user) => ({
    value: String(user.id),
    label: user?.employee?.full_name || user.username,
  }));

  // --- Dashboard metrics sourced directly from the backend summary block ---
  const statusColorMap = {
    completed: "#22c55e",
    cancelled: "#ef4444",
    pending: "#f59e0b",
    refunded: "#94a3b8",
    "on-hold": "#a855f7",
  };

  const summary = previewData?.summary;

  const dashboardStats = React.useMemo(() => {
    if (!summary) return null;

    const statusEntries = Object.entries(summary.by_status || {});
    const statusLabels = statusEntries.map(
      ([key]) => key.charAt(0).toUpperCase() + key.slice(1),
    );
    const statusTotals = statusEntries.map(([, v]) => parseFloat(v.total) || 0);
    const statusCounts = statusEntries.map(([, v]) => v.count || 0);
    const statusColors = statusEntries.map(
      ([key]) => statusColorMap[key] || "#6366f1",
    );

    return {
      revenue: summary.grand_total || 0,
      subtotal: summary.total_subtotal || 0,
      tax: summary.total_tax || 0,
      discount: summary.total_discount || 0,
      cogs: summary.total_cogs || 0,
      grossProfit: summary.gross_profit || 0,
      grossProfitMargin: summary.gross_profit_margin || 0,
      count: summary.total_items || 0,
      aov: summary.average_sale || 0,
      statusBarOptions: {
        chart: {
          type: "bar",
          toolbar: { show: false },
          background: "transparent",
        },
        xaxis: { categories: statusLabels },
        plotOptions: {
          bar: { borderRadius: 6, columnWidth: "45%", distributed: true },
        },
        dataLabels: { enabled: false },
        legend: { show: false },
        colors: statusColors,
        theme: { mode: isDarkMode ? "dark" : "light" },
      },
      statusBarSeries: [{ name: "Revenue", data: statusTotals }],
      statusPieOptions: {
        chart: { type: "donut", background: "transparent" },
        labels: statusLabels,
        theme: { mode: isDarkMode ? "dark" : "light" },
        plotOptions: { pie: { donut: { size: "70%" } } },
        dataLabels: { enabled: false },
        colors: statusColors,
      },
      statusPieSeries: statusCounts,
      currency: currencySymbol,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary, currencySymbol, isDarkMode]);

  return (
    <PageContainer>
      <PageHeader>
        <PageHeaderHeading
          title={
            <div className="flex flex-col">
              <div className="flex items-center gap-3 text-primary mb-2">
                <div className="bg-primary/10 rounded-xl p-1">
                  <FileText className="h-5 w-5" />
                </div>
                <span className="text-xs font-bold uppercase tracking-widest text-primary/80">
                  Performance Analytics
                </span>
              </div>
              <span className="text-3xl font-extrabold tracking-tight">
                Sales Summary Report
              </span>
            </div>
          }
          subtitle="Analyze your sales performance across stores, customers, and team members with advanced filtering and grouping."
        />
        <PageHeaderActions>
          <Button
            variant="outline"
            size="lg"
            onClick={handleClearFilters}
            className="rounded-xl mr-2"
          >
            <RefreshCcw className="mr-2 h-4 w-4" /> Reset
          </Button>
          {hasPermission("report sales summary view") && (
            <Button
              variant="secondary"
              size="lg"
              className="rounded-xl shadow-lg shadow-secondary/20 mr-2"
              onClick={handleGenerateReport}
              disabled={generateReportMutation.isPending}
            >
              {generateReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  Generate PDF
                </>
              )}
            </Button>
          )}
          {hasPermission("report sales summary view") && (
            <Button
              size="lg"
              className="rounded-xl shadow-lg shadow-primary/20"
              onClick={() => {
                showToast("Loading report data...", "info");
                previewReportMutation.mutate(filters);
              }}
              disabled={previewReportMutation.isPending}
            >
              {previewReportMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Generate Report
                </>
              )}
            </Button>
          )}
        </PageHeaderActions>
      </PageHeader>

      <div className="flex flex-col gap-4">
        {/* Main Filter Panel */}
        <Card className="border border-border/50 shadow-none overflow-hidden transition-all duration-300">
          <CardHeader className="bg-muted/50 border-b pb-4 pt-4 flex flex-row items-center justify-between space-y-0">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-background shadow-sm rounded-lg border">
                <Filter className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <CardTitle className="text-lg">Filtering Criteria</CardTitle>
                <CardDescription className="text-xs text-muted-foreground">
                  Define the customer, location, and date range
                </CardDescription>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className="text-xs font-semibold rounded-lg"
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
          </CardHeader>
          {showFilters && (
            <CardContent className="p-5">
              <div className="grid md:grid-cols-3 gap-6">
                {/* Date Scope */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-primary" /> Date Scope
                  </h3>
                  <div className="grid grid-cols-1 xl:grid-cols-1 gap-4">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Start Date
                      </Label>
                      <Input
                        type="datetime-local"
                        className="bg-muted/30 border-border focus:bg-background transition-colors"
                        name="start_date"
                        value={filters.start_date}
                        onChange={handleFilterChange}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">End Date</Label>
                      <Input
                        type="datetime-local"
                        className="bg-muted/30 border-border focus:bg-background transition-colors"
                        name="end_date"
                        value={filters.end_date}
                        onChange={handleFilterChange}
                      />
                    </div>
                  </div>

                  <div className="pt-3 border-t space-y-3">
                    <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                      <Layers className="h-4 w-4 text-primary" /> Grouping &
                      Order
                    </h3>
                    <div className="grid grid-cols-1 gap-4">
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">
                          Group By
                        </Label>
                        <Select
                          value={filters.group_by}
                          onValueChange={(val) =>
                            setFilters((p) => ({ ...p, group_by: val }))
                          }
                        >
                          <SelectTrigger className="bg-muted/30 border-border">
                            <SelectValue placeholder="None" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Default</SelectItem>
                            <SelectItem value="day">Daily</SelectItem>
                            <SelectItem value="week">Weekly</SelectItem>
                            <SelectItem value="month">Monthly</SelectItem>
                            <SelectItem value="store">By Store</SelectItem>
                            <SelectItem value="customer">
                              By Customer
                            </SelectItem>
                            <SelectItem value="user">By Team Member</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-muted-foreground">Sort By</Label>
                        <Select
                          value={filters.sort_by}
                          onValueChange={(val) =>
                            setFilters((p) => ({ ...p, sort_by: val }))
                          }
                        >
                          <SelectTrigger className="bg-muted/30 border-border">
                            <SelectValue placeholder="Date" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="date">
                              Transaction Date
                            </SelectItem>
                            <SelectItem value="total">Revenue Total</SelectItem>
                            <SelectItem value="customer">
                              Customer Name
                            </SelectItem>
                            <SelectItem value="store">Branch Name</SelectItem>
                            <SelectItem value="invoice_number">
                              Invoice ID
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Entity Filters */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" /> Entity Scope
                  </h3>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">Customer</Label>
                      <ReactSelect
                        options={customerOptions}
                        value={customerOptions.find(
                          (opt) => opt.value === String(filters.customer_id),
                        )}
                        onChange={(opt) =>
                          setFilters((p) => ({
                            ...p,
                            customer_id: opt?.value || "",
                          }))
                        }
                        placeholder="All Customers"
                        isClearable
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Store / Branch
                      </Label>
                      <ReactSelect
                        options={storeOptions}
                        value={
                          storeOptions.find(
                            (opt) => opt.value === String(filters.store_id || ""),
                          ) ||
                          (filters.store_id &&
                            currentStore?.id &&
                            String(filters.store_id) === String(currentStore.id)
                            ? {
                              value: String(currentStore.id),
                              label: currentStore.name,
                            }
                            : null)
                        }
                        onChange={(opt) =>
                          setFilters((p) => ({
                            ...p,
                            store_id: opt?.value || "",
                          }))
                        }
                        placeholder="All Locations"
                        isClearable={Boolean(filters.store_id)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Assigned User
                      </Label>
                      <ReactSelect
                        options={userOptions}
                        value={userOptions.find(
                          (opt) => opt.value === String(filters.user_id),
                        )}
                        onChange={(opt) =>
                          setFilters((p) => ({
                            ...p,
                            user_id: opt?.value || "",
                          }))
                        }
                        placeholder="All Staff Members"
                        isClearable
                      />
                    </div>
                  </div>
                </div>

                {/* Status Filters */}
                <div className="space-y-4">
                  <h3 className="text-sm font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
                    <Clock className="h-4 w-4 text-primary" /> Status & Metadata
                  </h3>
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Resolution Status
                      </Label>
                      <Select
                        value={filters.status}
                        onValueChange={(val) =>
                          setFilters((p) => ({ ...p, status: val }))
                        }
                      >
                        <SelectTrigger className="bg-muted/30">
                          <SelectValue placeholder="Any Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Default (Any)</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                          <SelectItem value="refunded">Refunded</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Payment Status
                      </Label>
                      <Select
                        value={filters.payment_status}
                        onValueChange={(val) =>
                          setFilters((p) => ({ ...p, payment_status: val }))
                        }
                      >
                        <SelectTrigger className="bg-muted/30">
                          <SelectValue placeholder="Any Status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Default (Any)</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="failed">Failed</SelectItem>
                          <SelectItem value="refunded">Refunded</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                          <SelectItem value="on-hold">On Hold</SelectItem>
                          <SelectItem value="partially-refunded">
                            Partially Refunded
                          </SelectItem>
                          <SelectItem value="partially-paid">
                            Partially Paid
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-muted-foreground">
                        Payment Method
                      </Label>
                      <Select
                        value={filters.payment_method}
                        onValueChange={(val) =>
                          setFilters((p) => ({ ...p, payment_method: val === "none" ? "" : val }))
                        }
                      >
                        <SelectTrigger className="bg-muted/30">
                          <SelectValue placeholder="Any Method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Default (Any)</SelectItem>
                          {availablePaymentMethods.map((pm) => (
                            <SelectItem key={pm.id || pm.slug || pm} value={pm.id || pm.slug || pm}>
                              {pm.name || pm}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="p-3 rounded-xl bg-muted/30 border border-dashed hover:border-primary/50 transition-colors group cursor-pointer inline-flex items-center justify-between w-full">
                      <div className="space-y-0.5">
                        <Label className="group-hover:text-primary transition-colors cursor-pointer">
                          Full Item Audit
                        </Label>
                        <p className="text-[10px] text-muted-foreground">
                          Include individual items in the PDF
                        </p>
                      </div>
                      <Switch
                        checked={filters.include_items}
                        onCheckedChange={(c) =>
                          setFilters((p) => ({ ...p, include_items: c }))
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {previewData && dashboardStats && (
          <div className="flex flex-col gap-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* KPI Cards - Row 1 */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="border-none shadow-lg ring-1 ring-border/50">
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      Grand Total
                    </p>
                    <h3 className="text-2xl font-bold text-foreground">
                      {dashboardStats.currency}
                      {dashboardStats.revenue.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </h3>
                  </div>
                  <div className="p-3 bg-primary/10 text-primary rounded-xl">
                    <DollarSign className="h-6 w-6" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg ring-1 ring-border/50">
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      Tax Collected
                    </p>
                    <h3 className="text-2xl font-bold text-foreground">
                      {dashboardStats.currency}
                      {dashboardStats.tax.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </h3>
                  </div>
                  <div className="p-3 bg-amber-100 text-amber-600 rounded-xl">
                    <Receipt className="h-6 w-6" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg ring-1 ring-border/50">
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      Total Sales
                    </p>
                    <h3 className="text-2xl font-bold text-foreground">
                      {dashboardStats.count}
                    </h3>
                  </div>
                  <div className="p-3 bg-green-100 text-green-600 rounded-xl">
                    <FileText className="h-6 w-6" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg ring-1 ring-border/50">
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      Avg. Sale Value
                    </p>
                    <h3 className="text-2xl font-bold text-foreground">
                      {dashboardStats.currency}
                      {dashboardStats.aov.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </h3>
                  </div>
                  <div className="p-3 bg-purple-100 text-purple-600 rounded-xl">
                    <TrendingUp className="h-6 w-6" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* KPI Cards - Row 2 (Profitability) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="border-none shadow-lg ring-1 ring-border/50">
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      Gross Profit
                    </p>
                    <h3 className="text-2xl font-bold text-foreground">
                      {dashboardStats.currency}
                      {dashboardStats.grossProfit.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      {dashboardStats.grossProfitMargin.toFixed(2)}% margin
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-100 text-emerald-600 rounded-xl">
                    <Percent className="h-6 w-6" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg ring-1 ring-border/50">
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      Total Discount
                    </p>
                    <h3 className="text-2xl font-bold text-foreground">
                      {dashboardStats.currency}
                      {dashboardStats.discount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </h3>
                  </div>
                  <div className="p-3 bg-rose-100 text-rose-600 rounded-xl">
                    <Receipt className="h-6 w-6" />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-none shadow-lg ring-1 ring-border/50">
                <CardContent className="p-6 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground mb-1">
                      Total COGS
                    </p>
                    <h3 className="text-2xl font-bold text-foreground">
                      {dashboardStats.currency}
                      {dashboardStats.cogs.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                      })}
                    </h3>
                  </div>
                  <div className="p-3 bg-slate-100 text-slate-600 rounded-xl">
                    <Package className="h-6 w-6" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <Card className="lg:col-span-2 shadow-xl border-none ring-1 ring-border/50 overflow-hidden">
                <CardHeader className="bg-muted/30 border-b pb-4">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg font-bold">
                      Revenue by Status
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <ReactApexChart
                    options={dashboardStats.statusBarOptions}
                    series={dashboardStats.statusBarSeries}
                    type="bar"
                    height={300}
                  />
                </CardContent>
              </Card>

              <Card className="shadow-xl border-none ring-1 ring-border/50 overflow-hidden">
                <CardHeader className="bg-muted/30 border-b pb-4">
                  <div className="flex items-center gap-2">
                    <PieChart className="h-5 w-5 text-primary" />
                    <CardTitle className="text-lg font-bold">
                      Sales by Status
                    </CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-6 flex items-center justify-center min-h-[300px]">
                  {dashboardStats.statusPieSeries.length > 0 ? (
                    <ReactApexChart
                      options={dashboardStats.statusPieOptions}
                      series={dashboardStats.statusPieSeries}
                      type="donut"
                      height={300}
                    />
                  ) : (
                    <p className="text-muted-foreground">No data available</p>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>

      {/* Modal is now a clean shadcn Dialog */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-md p-0 overflow-hidden border-none shadow-2xl">
          <div className="h-2 bg-primary" />
          <div className="p-8 space-y-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="h-20 w-20 bg-success/10 text-success rounded-full flex items-center justify-center shadow-inner">
                <CheckCircle className="h-10 w-10" />
              </div>
              <div className="space-y-1">
                <DialogTitle className="text-2xl font-bold">
                  Report Generated!
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  The Sales Summary Report is ready based on your specific
                  criteria and is waiting for your action.
                </DialogDescription>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Button
                onClick={handleDownloadPDF}
                size="lg"
                className="rounded-xl w-full"
              >
                <Download className="mr-2 h-4 w-4" /> Download
              </Button>
              <Button
                onClick={handlePrintPDF}
                variant="outline"
                size="lg"
                className="rounded-xl w-full"
              >
                <PrintIcon className="mr-2 h-4 w-4" /> Print
              </Button>
            </div>
          </div>
          <div className="bg-muted/50 p-4 text-center border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCloseModal}
              className="font-medium"
            >
              Dismiss
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
};
